import React, { useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useEstimationUnit } from '../analytics/estimation/estimationUnit';
import { WorkLogEntry } from '../../shared/types';

export type PrecomputedAuthorTypeRow = { author: string; type: string; hours: number };

interface DeveloperTimeChartProps {
  data: WorkLogEntry[];
  /** Server aggregates; when present, chart ignores `data` for series computation. */
  precomputedByAuthorType?: PrecomputedAuthorTypeRow[];
}

type ChartMode = 'total' | 'by-type';

// Preferred colors for common Jira types (match TaskTypeDistributionChart)
const TASK_TYPE_COLORS: Record<string, string> = {
  Story: '#63ba3c',
  Bug: '#e5493a',
  Task: '#4bade8',
  'Sub-task': '#4bade8',
  Epic: '#6554c0',
  'Bug (Sub-task)': '#e5493a',
  'Security Issue': '#000000',
  Security: '#000000',
  'UI/UX': '#f59e0b',
};

const DEFAULT_PALETTE = [
  '#8b5cf6',
  '#6366f1',
  '#06b6d4',
  '#10b981',
  '#f59e0b',
  '#ec4899',
  '#14b8a6',
  '#f97316',
];

const CANONICAL_TYPE_ORDER = ['Story', 'Task', 'Bug', 'Sub-task', 'Bug (Sub-task)', 'Epic'];

function isSubTaskStripeLabel(name: string): boolean {
  const lower = name.toLowerCase();
  return lower === 'sub-task' || lower === 'subtask';
}

function isBugSubtaskStripeLabel(name: string): boolean {
  return name.trim().toLowerCase() === 'bug (sub-task)';
}

function resolveCategory(raw: string | undefined): string {
  const t = (raw ?? '').toLowerCase();
  if (t.includes('story')) return 'Story';
  if (t === 'bug') return 'Bug';
  return raw?.trim() || 'Unknown';
}

function sortIssueTypes(types: string[], totals: Map<string, number>): string[] {
  return [...types].sort((a, b) => {
    const ia = CANONICAL_TYPE_ORDER.indexOf(a);
    const ib = CANONICAL_TYPE_ORDER.indexOf(b);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    const ha = totals.get(a) || 0;
    const hb = totals.get(b) || 0;
    if (hb !== ha) return hb - ha;
    return a.localeCompare(b);
  });
}

function assignColors(types: string[]): Record<string, string> {
  const out: Record<string, string> = {};
  let paletteIdx = 0;
  for (const t of types) {
    if (TASK_TYPE_COLORS[t]) {
      out[t] = TASK_TYPE_COLORS[t];
    } else {
      out[t] = DEFAULT_PALETTE[paletteIdx % DEFAULT_PALETTE.length];
      paletteIdx += 1;
    }
  }
  return out;
}

export const DeveloperTimeChart: React.FC<DeveloperTimeChartProps> = ({
  data,
  precomputedByAuthorType,
}) => {
  const [mode, setMode] = useState<ChartMode>('by-type');
  const { toUnitValue, suffix } = useEstimationUnit();

  const { issueTypeOrder, colorByType, stackedData, totalTimeData } = useMemo(() => {
    const usePre = precomputedByAuthorType != null && precomputedByAuthorType.length > 0;
    const totals = new Map<string, number>();

    if (usePre) {
      for (const row of precomputedByAuthorType!) {
        const t = resolveCategory(row.type);
        totals.set(t, (totals.get(t) || 0) + row.hours);
      }
    } else {
      for (const entry of data) {
        const t = resolveCategory(entry.type);
        totals.set(t, (totals.get(t) || 0) + entry.timeSpent);
      }
    }

    const rawTypes = Array.from(totals.keys());
    const types = sortIssueTypes(rawTypes, totals);
    const colors = assignColors(types);

    const byAuthor = new Map<string, Record<string, number | string>>();
    if (usePre) {
      for (const row of precomputedByAuthorType!) {
        const author = row.author;
        const t = resolveCategory(row.type);
        const h = row.hours;
        if (!byAuthor.has(author)) {
          const rec: Record<string, number | string> = { author, total: 0 };
          for (const typ of types) {
            rec[typ] = 0;
          }
          byAuthor.set(author, rec);
        }
        const r = byAuthor.get(author)!;
        r.total = (r.total as number) + h;
        r[t] = (r[t] as number) + h;
      }
    } else {
      for (const entry of data) {
        const author = entry.author;
        const t = resolveCategory(entry.type);
        if (!byAuthor.has(author)) {
          const rec: Record<string, number | string> = { author, total: 0 };
          for (const typ of types) {
            rec[typ] = 0;
          }
          byAuthor.set(author, rec);
        }
        const r = byAuthor.get(author)!;
        r.total = (r.total as number) + entry.timeSpent;
        r[t] = (r[t] as number) + entry.timeSpent;
      }
    }

    const stacked = Array.from(byAuthor.values()).sort(
      (a, b) => (b.total as number) - (a.total as number)
    );

    const totalByAuthor = new Map<string, number>();
    for (const row of stacked) {
      totalByAuthor.set(String(row.author), row.total as number);
    }
    const totalTime = Array.from(totalByAuthor.entries())
      .map(([author, timeSpent]) => ({ author, timeSpent }))
      .sort((a, b) => b.timeSpent - a.timeSpent);

    return {
      issueTypeOrder: types,
      colorByType: colors,
      stackedData: stacked,
      totalTimeData: totalTime,
    };
  }, [data, precomputedByAuthorType]);

  const convertedStackedData = useMemo(
    () =>
      stackedData.map((row) => {
        const converted: Record<string, number | string> = { author: row.author };
        for (const key of Object.keys(row)) {
          if (key === 'author') continue;
          converted[key] = toUnitValue(row[key] as number);
        }
        return converted;
      }),
    [stackedData, toUnitValue]
  );

  const convertedTotalTimeData = useMemo(
    () => totalTimeData.map((r) => ({ author: r.author, timeSpent: toUnitValue(r.timeSpent) })),
    [totalTimeData, toUnitValue]
  );

  // Types for Recharts tooltip
  interface TooltipPayloadItem {
    value?: number;
    dataKey?: string;
    color?: string;
  }

  interface TooltipProps {
    active?: boolean;
    payload?: TooltipPayloadItem[];
    label?: string;
  }

  const typeOrderIndex = (key: string | undefined): number => {
    if (!key) return 999;
    const i = issueTypeOrder.indexOf(key);
    return i !== -1 ? i : 999;
  };

  const StackedTooltip = ({ active, payload, label }: TooltipProps) => {
    if (active && payload && payload.length) {
      const total = payload.reduce(
        (sum: number, item: TooltipPayloadItem) => sum + (item.value || 0),
        0
      );
      return (
        <div
          style={{
            backgroundColor: 'var(--color-bg-elevated)',
            padding: '12px',
            border: '1px solid var(--color-border-strong)',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
          }}
        >
          <p
            style={{
              margin: '0 0 8px 0',
              fontWeight: 'bold',
              fontSize: '14px',
              color: 'var(--color-text)',
            }}
          >
            Team Member: {label}
          </p>
          <p
            style={{ margin: '0 0 8px 0', color: 'var(--color-text-secondary)', fontSize: '12px' }}
          >
            Total: {total.toFixed(1)} {suffix}.
          </p>
          <div
            style={{
              marginTop: '8px',
              paddingTop: '8px',
              borderTop: '1px solid var(--color-border)',
            }}
          >
            {payload
              .filter((item: TooltipPayloadItem) => (item.value || 0) > 0)
              .sort(
                (a: TooltipPayloadItem, b: TooltipPayloadItem) =>
                  typeOrderIndex(a.dataKey) - typeOrderIndex(b.dataKey)
              )
              .map((item: TooltipPayloadItem) => {
                const itemValue = item.value || 0;
                const percentage = total > 0 ? ((itemValue / total) * 100).toFixed(1) : '0';
                const name = item.dataKey || '';
                const isSubtask = isSubTaskStripeLabel(name);
                const isBugSubSt = isBugSubtaskStripeLabel(name);
                const swatchColor = colorByType[name] || item.color || '#6b7280';
                return (
                  <p key={item.dataKey || 'unknown'} style={{ margin: '4px 0', fontSize: '12px' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        width: '12px',
                        height: '12px',
                        backgroundColor: isSubtask || isBugSubSt ? '#ffffff' : swatchColor,
                        backgroundImage: isSubtask
                          ? 'repeating-linear-gradient(45deg, #4bade8, #4bade8 2px, #ffffff 2px, #ffffff 4px)'
                          : isBugSubSt
                            ? 'repeating-linear-gradient(45deg, #e5493a, #e5493a 2px, #ffffff 2px, #ffffff 4px)'
                            : 'none',
                        border: isSubtask
                          ? '1px solid #4bade8'
                          : isBugSubSt
                            ? '1px solid #e5493a'
                            : 'none',
                        marginRight: '8px',
                        borderRadius: '2px',
                        verticalAlign: 'middle',
                      }}
                    />
                    {item.dataKey}: {itemValue.toFixed(1)} {suffix}. ({percentage}%)
                  </p>
                );
              })}
          </div>
        </div>
      );
    }
    return null;
  };

  const lastStackType = issueTypeOrder.length > 0 ? issueTypeOrder[issueTypeOrder.length - 1] : '';

  return (
    <div
      className="dashboard-card recharts-bar-chart"
      title="Breakdown of time spent by each team member. Useful for understanding individual contributions and team workload distribution."
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <h3 style={{ margin: 0 }}>
          Time Registered by Team Member
          <span
            style={{
              marginLeft: '0.5rem',
              fontSize: '0.75rem',
              color: 'var(--color-text-secondary)',
              fontWeight: 'normal',
            }}
            title="Individual team member productivity comparison. Switch between total time and breakdown by task types."
          >
            ℹ️
          </span>
        </h3>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setMode('total')}
            style={{
              padding: '6px 12px',
              fontSize: '14px',
              fontWeight: mode === 'total' ? '600' : '400',
              backgroundColor: mode === 'total' ? '#10b981' : 'var(--color-bg-surface)',
              color: mode === 'total' ? '#ffffff' : 'var(--color-text)',
              border: '1px solid',
              borderColor: mode === 'total' ? '#10b981' : 'var(--color-border)',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            Total
          </button>
          <button
            onClick={() => setMode('by-type')}
            style={{
              padding: '6px 12px',
              fontSize: '14px',
              fontWeight: mode === 'by-type' ? '600' : '400',
              backgroundColor: mode === 'by-type' ? '#10b981' : 'var(--color-bg-surface)',
              color: mode === 'by-type' ? '#ffffff' : 'var(--color-text)',
              border: '1px solid',
              borderColor: mode === 'by-type' ? '#10b981' : 'var(--color-border)',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            By Type
          </button>
        </div>
      </div>
      <div
        style={{
          backgroundColor: 'var(--color-bg-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '14px',
          color: 'var(--color-text)',
        }}
      >
        <span>
          Grand total:{' '}
          <strong>
            {toUnitValue(totalTimeData.reduce((sum, r) => sum + r.timeSpent, 0)).toFixed(1)}{' '}
            {suffix}
          </strong>
        </span>
        <span>
          Team members: <strong>{totalTimeData.length}</strong>
        </span>
      </div>
      <div style={{ height: '300px', overflow: 'visible' }}>
        <ResponsiveContainer width="100%" height="100%">
          {mode === 'total' ? (
            <BarChart
              data={convertedTotalTimeData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="author"
                tick={{ fontSize: 10 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis
                label={{ value: `Time Spent (${suffix})`, angle: -90, position: 'insideLeft' }}
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--color-bg-elevated)',
                  border: '1px solid var(--color-border-strong)',
                  borderRadius: '6px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
                  color: 'var(--color-text)',
                }}
                labelStyle={{ color: 'var(--color-text)', fontWeight: 'bold' }}
                itemStyle={{ color: 'var(--color-text-secondary)' }}
                formatter={(value: number) => [`${value.toFixed(1)} ${suffix}`, 'Time']}
                labelFormatter={(label) => `Team Member: ${label}`}
              />
              <Bar dataKey="timeSpent" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          ) : (
            <BarChart
              data={convertedStackedData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <defs>
                <pattern
                  id="subtask-stripe-chart"
                  x="0"
                  y="0"
                  width="8"
                  height="8"
                  patternUnits="userSpaceOnUse"
                >
                  <rect width="8" height="8" fill="#ffffff" />
                  <path
                    d="M 0,0 L 8,8 M -2,2 L 2,6 M 6,2 L 10,6"
                    stroke="#4bade8"
                    strokeWidth="1.5"
                  />
                </pattern>
                <pattern
                  id="bug-subtask-stripe-chart"
                  x="0"
                  y="0"
                  width="8"
                  height="8"
                  patternUnits="userSpaceOnUse"
                >
                  <rect width="8" height="8" fill="#ffffff" />
                  <path
                    d="M 0,0 L 8,8 M -2,2 L 2,6 M 6,2 L 10,6"
                    stroke="#e5493a"
                    strokeWidth="1.5"
                  />
                </pattern>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="author"
                tick={{ fontSize: 10 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis
                label={{ value: `Time Spent (${suffix})`, angle: -90, position: 'insideLeft' }}
                tick={{ fontSize: 12 }}
              />
              <Tooltip content={<StackedTooltip />} />
              {issueTypeOrder.map((taskType) => (
                <Bar
                  key={taskType}
                  dataKey={taskType}
                  stackId="a"
                  fill={
                    isSubTaskStripeLabel(taskType)
                      ? 'url(#subtask-stripe-chart)'
                      : isBugSubtaskStripeLabel(taskType)
                        ? 'url(#bug-subtask-stripe-chart)'
                        : colorByType[taskType] || '#6b7280'
                  }
                  radius={taskType === lastStackType ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                />
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
      {mode === 'by-type' && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '16px',
            marginTop: '20px',
            paddingTop: '16px',
            borderTop: '1px solid var(--color-border)',
          }}
        >
          {issueTypeOrder.map((taskType) => {
            const fill = colorByType[taskType] || '#6b7280';
            const isSub = isSubTaskStripeLabel(taskType);
            const isBugSub = isBugSubtaskStripeLabel(taskType);
            return (
              <div
                key={taskType}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                }}
              >
                <span
                  style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '4px',
                    backgroundColor: isSub || isBugSub ? '#ffffff' : fill,
                    backgroundImage: isSub
                      ? 'repeating-linear-gradient(45deg, #4bade8, #4bade8 2px, #ffffff 2px, #ffffff 4px)'
                      : isBugSub
                        ? 'repeating-linear-gradient(45deg, #e5493a, #e5493a 2px, #ffffff 2px, #ffffff 4px)'
                        : 'none',
                    border: isSub ? '1px solid #4bade8' : isBugSub ? '1px solid #e5493a' : 'none',
                    display: 'inline-block',
                  }}
                />
                <span style={{ color: 'var(--color-text)' }}>{taskType}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DeveloperTimeChart;
